import { Button, Label, Progress } from '@startupquickstart/frontend';
import { FileSpreadsheet, Upload, X, CheckCircle } from 'lucide-react';
import { useCallback, useRef, useState } from 'react';
import { toast } from '@startupquickstart/frontend';
import { cn } from '@/lib/utils';

interface CsvDropzoneProps {
  onFileSelect: (file: File) => void;
  selectedFile?: File | null;
  maxSizeMB?: number;
  isUploading?: boolean;
  uploadProgress?: number;
  disabled?: boolean;
}

export function CsvDropzone({
  onFileSelect,
  selectedFile,
  maxSizeMB = 10,
  isUploading = false,
  uploadProgress = 0,
  disabled = false,
}: CsvDropzoneProps) {
  const [isDragging, setIsDragging] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const maxSizeBytes = maxSizeMB * 1024 * 1024;

  const validateFile = useCallback(
    (file: File): string | null => {
      const validTypes = ['text/csv', 'application/vnd.ms-excel'];
      const extension = file.name.toLowerCase().split('.').pop();

      if (!validTypes.includes(file.type) && extension !== 'csv') {
        return 'Please upload a CSV file';
      }
      if (file.size > maxSizeBytes) {
        return `File size must be less than ${maxSizeMB}MB`;
      }
      return null;
    },
    [maxSizeBytes, maxSizeMB]
  );

  const handleFileSelect = useCallback(
    (file: File) => {
      const error = validateFile(file);
      if (error) {
        toast.error(error);
        return;
      }
      onFileSelect(file);
    },
    [validateFile, onFileSelect]
  );

  const handleDragOver = useCallback(
    (e: React.DragEvent<HTMLDivElement>) => {
      e.preventDefault();
      e.stopPropagation();
      if (!disabled && !isUploading) {
        setIsDragging(true);
      }
    },
    [disabled, isUploading]
  );

  const handleDragLeave = useCallback((e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
  }, []);

  const handleDrop = useCallback(
    (e: React.DragEvent<HTMLDivElement>) => {
      e.preventDefault();
      e.stopPropagation();
      setIsDragging(false);

      if (disabled || isUploading) return;

      const file = e.dataTransfer.files[0];
      if (file) {
        handleFileSelect(file);
      }
    },
    [disabled, isUploading, handleFileSelect]
  );

  const handleInputChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const file = e.target.files?.[0];
      if (file) {
        handleFileSelect(file);
      }
    },
    [handleFileSelect]
  );

  const handleClick = useCallback(() => {
    if (!disabled && !isUploading) {
      fileInputRef.current?.click();
    }
  }, [disabled, isUploading]);

  const handleClear = useCallback(
    (e: React.MouseEvent) => {
      e.stopPropagation();
      onFileSelect(null as unknown as File);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    },
    [onFileSelect]
  );

  const formatFileSize = (bytes: number) => {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(2)} MB`;
  };

  return (
    <div className="grid gap-2">
      <Label className="text-sm font-medium">CSV File</Label>
      <input
        ref={fileInputRef}
        type="file"
        accept=".csv,text/csv,application/vnd.ms-excel"
        onChange={handleInputChange}
        className="hidden"
        disabled={disabled || isUploading}
      />

      <div
        onClick={handleClick}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
        className={cn(
          'relative cursor-pointer rounded-xl border-2 border-dashed transition-all duration-200',
          'min-h-[220px] flex flex-col items-center justify-center gap-4 p-8',
          isDragging && 'border-primary bg-primary/5 scale-[1.02]',
          !isDragging && !selectedFile && 'border-border bg-muted/30 hover:border-primary/50 hover:bg-muted/50',
          selectedFile && !isUploading && 'border-solid border-emerald-200 bg-emerald-50/50 dark:border-emerald-800 dark:bg-emerald-950/30',
          (disabled || isUploading) && 'pointer-events-none opacity-50'
        )}
      >
        {isUploading ? (
          <div className="flex flex-col items-center gap-4">
            {/* Spinning ring */}
            <div className="relative w-16 h-16">
              <div className="absolute inset-0 rounded-full border-2 border-primary/20" />
              <div className="absolute inset-0 rounded-full border-2 border-primary border-t-transparent animate-spin" />
              <div className="absolute inset-2 rounded-full bg-primary/10 flex items-center justify-center">
                <Upload className="h-6 w-6 text-primary" />
              </div>
            </div>

            <div className="text-center space-y-3 w-full max-w-xs">
              <p className="font-medium">Uploading...</p>
              <Progress value={uploadProgress} className="h-1.5" />
              <p className="text-sm text-muted-foreground">{uploadProgress}%</p>
            </div>
          </div>
        ) : selectedFile ? (
          <div className="flex flex-col items-center gap-4">
            {/* File icon with checkmark */}
            <div className="relative">
              <div className="w-16 h-16 rounded-xl bg-emerald-100 dark:bg-emerald-900/50 flex items-center justify-center">
                <FileSpreadsheet className="h-8 w-8 text-emerald-600 dark:text-emerald-400" />
              </div>
              <div className="absolute -bottom-1 -right-1 w-6 h-6 rounded-full bg-emerald-500 flex items-center justify-center">
                <CheckCircle className="h-4 w-4 text-white" />
              </div>
            </div>

            <div className="text-center space-y-1">
              <p className="font-medium truncate max-w-[200px]">{selectedFile.name}</p>
              <p className="text-sm text-muted-foreground">
                {formatFileSize(selectedFile.size)}
              </p>
            </div>

            <Button
              type="button"
              variant="ghost"
              size="sm"
              onClick={handleClear}
              className="absolute top-3 right-3 text-muted-foreground hover:text-foreground"
            >
              <X className="h-4 w-4" />
              <span className="sr-only">Remove file</span>
            </Button>
          </div>
        ) : (
          <div className="flex flex-col items-center gap-4">
            {/* Icon with background ring */}
            <div className="w-16 h-16 rounded-full bg-primary/10 flex items-center justify-center">
              <FileSpreadsheet className="h-8 w-8 text-primary" />
            </div>

            <div className="text-center space-y-2">
              <p className="text-base font-medium">
                <span className="text-primary">Click to upload</span>
                <span className="text-muted-foreground"> or drag and drop</span>
              </p>
              <p className="text-sm text-muted-foreground">
                CSV files up to {maxSizeMB}MB
              </p>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
